import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { z } from "zod"
import { AuthorGroupPrivate, AuthorGroupsOrderBy } from "../../client"
import { lazy, Suspense, useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import Navbar from "../../components/Common/Navbar"
import {
  Avatar,
  AvatarGroup,
  Box,
  Center,
  Flex,
  SkeletonText,
  Spinner,
  Table,
  useDisclosure
} from "@chakra-ui/react"
import Sort from "../../components/Common/Sort"
import ActionsMenu from "../../components/Common/ActionsMenu"
import { PaginationFooter } from "../../components/Common/PaginationFooter"
import TabsList from "../../components/Common/TabsList"
import { userHasPermission } from "../../utils"
import { NoPermission } from "../../components/UI/no-permission"
import { readAuthorgroups } from "../../api/authorgroups"

const DetailAuthorGroup = lazy(() => import("../../components/AuthorGroups/DetailAuthorGroup"))
const AddAuthorGroup = lazy(() => import("../../components/AuthorGroups/AddAuthorGroup"))
const PER_PAGE = 25

const authorGroupsSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/authorgroups")({
  component: AuthorGroups,
  validateSearch: (search) => authorGroupsSearchSchema.parse(search),
})


function AuthorGroupsTable() {
  // Permission check
  const [ag_create, ag_read, ag_update, ag_delete] = useMemo(() => {
    return ["create", "read", "update", "delete"].map(
      (action) => userHasPermission([{ perm: `authorgroups_${action}`, is_allowed: false }])[0].is_allowed
    )
  }, [userHasPermission])

  if(!ag_read) return <NoPermission/>

  const fields = [
    { title: "Name", col: "name", align: undefined },
    { title: "Status", col: "is_active", align: undefined },
    { title: "Autoren", col: null, align: undefined },
    { title: "Aktionen", col: null, align: "right" as const },
  ]

  const [searchValue, setSearchValue] = useState<string>("")

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value)
  }

  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setPage = (page: number) => navigate({
    search: (prev: { [key: string]: string }) => ({ ...prev, page }),
  })

  // Set page on search value change
  const [prevPage, setPrevPage] = useState<number>(page)
  useEffect(() => {
    if(searchValue == "") {
      setPage(prevPage)
    } else {
      setPrevPage(page)
      setPage(1)
    }
  }, [searchValue])

  // Sorting
  const [columnName, setColumnName] = useState<AuthorGroupsOrderBy>()
  const [orderBy, setOrderBy] = useState<string>("")
  const [isDesc, setIsDesc] = useState<boolean>()

  // Query
  const queryOptions = useMemo(() =>
    readAuthorgroups({
      per_page: PER_PAGE,
      page: page,
      order_by: columnName,
      descending: isDesc,
      query: searchValue,
    }),
  [page, columnName, isDesc, searchValue])

  const {
    data: authorgroups,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...queryOptions,
    placeholderData: (prevData) => prevData,
  })

  // Sorting
  const sortTable = (name: AuthorGroupsOrderBy) => {
    if (columnName !== name) {
      setColumnName(name)
      setOrderBy("")
      sorting("")
    } else {
      sorting(orderBy)
    }
  }

  const sorting = (order: string) => {
    switch (order) {
      case "":
        setOrderBy("desc")
        setIsDesc(true)
        break
      case "desc":
        setOrderBy("asc")
        setIsDesc(false)
        break
      case "asc":
        setOrderBy("")
        setIsDesc(undefined)
        setColumnName(undefined)
        break
    }
  }

  // Open media detail view
  const detailModal = useDisclosure()
  const [detailAuthorgroup, setDetailAuthorgroup] = useState<AuthorGroupPrivate | null>(null)

  const openModal = (author: AuthorGroupPrivate) => {
    setDetailAuthorgroup(author)
    detailModal.onOpen()
  }

  return (
    <>
      <Navbar
        type="Autorengruppe"
        addAllowed={ag_create}
        addModalAs={AddAuthorGroup}
        showSearch={true}
        onSearchChange={handleSearchValueChange}
      />

      {detailModal.open &&
        <Suspense fallback={
          <Center
            bg="rgba(0, 0, 0, 0.3)"
            position="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            zIndex="10000"
          >
            <Spinner size="lg" color="white" />
          </Center>}
        >
          <DetailAuthorGroup
            isOpen={detailModal.open}
            onClose={detailModal.onClose}
            authorgroup={detailAuthorgroup}
          />
        </Suspense>
      }

      <Table.ScrollArea asChild>
        <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={"table"}>
          <Table.Header>
            <Table.Row>
              {fields.map((field, i) =>
                field.col != null ? (
                  <Table.ColumnHeader
                    key={i}
                    onClick={() => sortTable(field.col as AuthorGroupsOrderBy)}
                    cursor="pointer"
                    textAlign={field.align}
                  >
                    {field.title}
                    {columnName === field.col &&
                      <Sort
                        columnNamePassed={columnName}
                        orderByPassed={orderBy}
                      />
                    }
                  </Table.ColumnHeader>
                ) : (
                  <Table.ColumnHeader key={field.title} textAlign={field.align}>
                    {field.title}
                  </Table.ColumnHeader>
                )
              )}
            </Table.Row>
          </Table.Header>
          {isPending ? (
            <Table.Body>
              <Table.Row>
                {new Array(4).fill(null).map((_, index) => (
                  <Table.Cell key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          ) : (
            <Table.Body>
              {!authorgroups?.data ? (
                <Table.Row>
                  <Table.Cell colSpan={4} textAlign="center">
                    Keine Autorengruppen verfügbar
                  </Table.Cell>
                </Table.Row>
              ) : (
                authorgroups?.data.map((authorgroup, i) => (
                  <Table.Row key={i} opacity={isPlaceholderData ? 0.5 : 1}>
                    <Table.Cell maxW={{md: "35em"}} onClick={() => openModal(authorgroup)} cursor="pointer">
                      {authorgroup.name}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(authorgroup)} cursor="pointer">
                      <Flex gap={2}>
                        <Box
                          w="2"
                          h="2"
                          borderRadius="50%"
                          bg={authorgroup.is_active ? "success" : "danger"}
                          alignSelf="center"
                        />
                        {authorgroup.is_active ? "Aktiv" : "Inaktiv"}
                      </Flex>
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(authorgroup)} cursor="pointer">
                      <AvatarGroup gap="0" spaceX="-3" size="md">
                        {authorgroup.fk_authors?.slice(0, 3).map((author, index) => (
                          <Avatar.Root key={index}>
                            <Avatar.Fallback
                              name={
                                author?.lastname && author?.firstname
                                  ? `${author.lastname} ${author.firstname}`
                                  : author?.firstname || author?.lastname || author?.abbreviation || ""
                              }
                            />
                            <Avatar.Image src={author.fk_media_profile_pic?.path.replace(/-(LG)\./, "-SM.")} />
                          </Avatar.Root>
                        ))}

                        {authorgroup.fk_authors && authorgroup.fk_authors.length > 3 && (
                          <Avatar.Root variant="solid">
                            <Avatar.Fallback name={`+ ${authorgroup.fk_authors.length - 3}`} />
                          </Avatar.Root>
                        )}
                      </AvatarGroup>
                    </Table.Cell>

                    <Table.Cell textAlign="right">
                      <ActionsMenu
                        type="Autorengruppe"
                        value={authorgroup}
                        disableDelete={authorgroup.fk_authors?.length != 0 || !ag_delete}
                        disableEdit={!ag_update}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          )}
        </Table.Root>
      </Table.ScrollArea>
      <PaginationFooter
        onChangePage={setPage}
        page={page}
        count={authorgroups?.count ? authorgroups?.count : 0}
        perPage={PER_PAGE}
      />
    </>
  )
}

export default AuthorGroupsTable

function AuthorGroups() {
  const tabs = useMemo(() => [
    {value: "authors", title: "Autoren"},
    {value: "authorgroups", title: "Autorengruppen"},
  ], [])

  return (
    <TabsList tabs={tabs} defaultVal="authors" />
  )
}
